import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { UserProvider } from "@/context/UserContext";
import { LanguageProvider } from "@/context/LanguageContext";

// Critical pages - load immediately
import HomePage from "@/pages/HomePage";

// Secondary pages - lazy load
const EventsPage = lazy(() => import("@/pages/EventsPage"));
const EventPage = lazy(() => import("@/pages/EventPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const YearlyCalendarPage = lazy(() => import("@/pages/YearlyCalendarPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const BookmarksPage = lazy(() => import("@/pages/BookmarksPage"));
const FreeEventsPage = lazy(() => import("@/pages/FreeEventsPage"));
const MonthlyEventsPage = lazy(() => import("@/pages/MonthlyEventsPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogArticlePage = lazy(() => import("@/pages/BlogArticlePage"));
const ToolsPage = lazy(() => import("@/pages/ToolsPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const ReferralPage = lazy(() => import("@/pages/ReferralPage"));
const FamilyEventsPage = lazy(() => import("@/pages/FamilyEventsPage"));

// Tool pages - lazy load (heavy components)
const EventQuizTool = lazy(() => import("@/pages/tools/EventQuizTool"));
const BudgetPlannerTool = lazy(() => import("@/pages/tools/BudgetPlannerTool"));
const TripPlannerTool = lazy(() => import("@/pages/tools/TripPlannerTool"));
const EventComparisonTool = lazy(() => import("@/pages/tools/EventComparisonTool"));
const BestMonthTool = lazy(() => import("@/pages/tools/BestMonthTool"));
const PackingChecklistTool = lazy(() => import("@/pages/tools/PackingChecklistTool"));
const TransportPlannerTool = lazy(() => import("@/pages/tools/TransportPlannerTool"));
const GroupVotingTool = lazy(() => import("@/pages/tools/GroupVotingTool"));
const WeatherCheckerTool = lazy(() => import("@/pages/tools/WeatherCheckerTool"));
const HotelFinderTool = lazy(() => import("@/pages/tools/HotelFinderTool"));
const PhotoSpotsTool = lazy(() => import("@/pages/tools/PhotoSpotsTool"));

// Defer non-critical UI components - load after initial render
const LiveVisitorCounter = lazy(() => import("@/components/LiveVisitorCounter"));
const ActivityFeed = lazy(() => import("@/components/ActivityFeed"));

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground text-sm">Loading...</p>
      </div>
    </div>
  );
}

// Deferred components wrapper - loads after 2 seconds
function DeferredComponents() {
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 2000);
    return () => clearTimeout(timer);
  }, []);
  
  if (!show) return null;
  
  return (
    <Suspense fallback={null}>
      <LiveVisitorCounter />
      <ActivityFeed compact />
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <UserProvider>
          <div className="App min-h-screen bg-background">
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Critical route - no lazy loading */}
                  <Route path="/" element={<HomePage />} />
                  
                  {/* All other routes - lazy loaded */}
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/event/:eventId" element={<EventPage />} />
                  <Route path="/calendar" element={<CalendarPage />} />
                  <Route path="/calendar/yearly" element={<YearlyCalendarPage />} />
                  <Route path="/events/:monthYear" element={<MonthlyEventsPage />} />
                  <Route path="/free-events" element={<FreeEventsPage />} />
                  <Route path="/family-events" element={<FamilyEventsPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/blog/:slug" element={<BlogArticlePage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/bookmarks" element={<BookmarksPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/referral" element={<ReferralPage />} />
                  
                  {/* Tool routes - lazy loaded */}
                  <Route path="/tools" element={<ToolsPage />} />
                  <Route path="/tools/quiz" element={<EventQuizTool />} />
                  <Route path="/tools/budget" element={<BudgetPlannerTool />} />
                  <Route path="/tools/trip" element={<TripPlannerTool />} />
                  <Route path="/tools/compare" element={<EventComparisonTool />} />
                  <Route path="/tools/bestmonth" element={<BestMonthTool />} />
                  <Route path="/tools/packing" element={<PackingChecklistTool />} />
                  <Route path="/tools/transport" element={<TransportPlannerTool />} />
                  <Route path="/tools/vote" element={<GroupVotingTool />} />
                  <Route path="/tools/weather" element={<WeatherCheckerTool />} />
                  <Route path="/tools/hotel" element={<HotelFinderTool />} />
                  <Route path="/tools/photo" element={<PhotoSpotsTool />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
            <DeferredComponents />
            <Toaster position="top-right" richColors />
          </div>
        </UserProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
